/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { TFunction } from 'i18next';
import { CATEGORY, SCREEN_NAME } from '../config/analytics';
import {
  SSO_METHOD,
  SSO_OTP_CHANNEL,
  type SsoAuthMethod,
} from './loginSignupAnalyticsUtils';
import {
  Purpose,
  type PhoneVerificationChannel,
  type UserUpdateRequestBody,
} from '../managers/sso';

/** Countdown / resend label for VerifyPhone OTP timer. */
export function getVerifyPhoneTimerText(
  t: TFunction,
  timer: number,
  expiredText?: string,
): string {
  if (timer > 0) {
    return String(t('verifyPhonePage.requestOtpCountdown')).replace(
      '%s',
      `${timer}`,
    );
  }
  return expiredText ?? String(t('verifyPhonePage.requestNewOtpText'));
}

/** Optional profile fields applied after phone verification succeeds. */
export type PostVerificationProfileFields = {
  firstName?: string;
  lastName?: string;
  gender?: string;
  dateOfBirth?: string;
};

/** Optional profile fields applied after phone verification succeeds. */
export function buildPostVerificationProfileBody(
  fields: PostVerificationProfileFields = {},
): UserUpdateRequestBody {
  return {
    ...(fields.firstName ? { firstName: fields.firstName } : {}),
    ...(fields.lastName ? { lastName: fields.lastName } : {}),
    ...(fields.gender ? { gender: fields.gender } : {}),
    ...(fields.dateOfBirth ? { dateOfBirth: fields.dateOfBirth } : {}),
  };
}

export function resolveSsoOtpChannel(
  channels?: PhoneVerificationChannel[],
): (typeof SSO_OTP_CHANNEL)[keyof typeof SSO_OTP_CHANNEL] {
  if (channels?.includes('whatsapp') && !channels.includes('sms')) {
    return SSO_OTP_CHANNEL.whatsapp;
  }
  return SSO_OTP_CHANNEL.sms;
}

export function resolveMobileOtpAnalytics(
  purpose: Purpose,
  channels?: PhoneVerificationChannel[],
  /** Sign-up method. Defaults to emailOnly. */
  signupMethod?: SsoAuthMethod,
) {
  const isLogin = purpose === Purpose.LOGIN_2FA;
  const isSignup = purpose === Purpose.PHONE_VERIFICATION;

  return {
    enabled: isLogin || isSignup,
    isLogin,
    category: isLogin ? CATEGORY.ssoLogin : CATEGORY.ssoSignup,
    screenName: SCREEN_NAME.mobileOTPv2,
    method: isLogin
      ? SSO_METHOD.mobilePhoneLogin
      : (signupMethod ?? SSO_METHOD.emailOnly),
    verificationMethod: resolveSsoOtpChannel(channels),
  };
}
